import { useEffect, useTransition } from "react";

import { SteppedMultiColumnDropdown } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { useSteppedMultiColumnDropdown } from "../../hooks/useSteppedMultiColumnDropdown";
import { FilterCriteriaCard } from "../FilterCriteriaCard";
import {
  createPathAwareDropdown,
  pathOperators,
  column,
  OPERATORS,
  conditions,
} from "./DefinitionBuilder";
import { useProviderFilterCombiner } from "./hooks";

import type { BindingCriteria_bindings$key } from "./__generated__/BindingCriteria_bindings.graphql";
import type { CriteriaFilterValue } from "@stacklet/ui";

interface Props {
  bindingsRef: BindingCriteria_bindings$key;
  handleSelectionsChange?: (selections: CriteriaFilterValue[]) => void;
  initialCriteriaFilters?: CriteriaFilterValue[];
}

const DEFAULT_SELECTIONS = ["binding-uuid", "is-one-of"];

export function BindingCriteria({
  bindingsRef,
  handleSelectionsChange,
  initialCriteriaFilters,
}: Props) {
  const [, startTransition] = useTransition();

  const { combineFilters } = useProviderFilterCombiner({
    fieldName: "provider",
    operator: "equals",
  });

  // Use pagination fragment for bindings
  const {
    data: bindingsData,
    loadNext: loadNextBindings,
    hasNext: hasNextBindings,
    refetch: refetchBindings,
  } = usePaginationFragment(
    graphql`
      fragment BindingCriteria_bindings on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "BindingCriteriaBindingsPaginationQuery") {
        bindings(first: $count, after: $cursor, filterElement: $filter)
          @connection(key: "BindingCriteria__bindings") {
          edges {
            node {
              uuid
              name
            }
          }
        }
      }
    `,
    bindingsRef,
  );

  // Transform data for the dropdown (Relay automatically merges edges with @connection)
  const bindingItems =
    bindingsData.bindings?.edges.map((edge) => ({
      id: edge.node.uuid,
      name: edge.node.name,
    })) ?? [];

  // Refetch data when providers change
  useEffect(() => {
    startTransition(() => {
      const filter = combineFilters(null);
      refetchBindings({ filter });
    });
  }, [combineFilters, refetchBindings]);

  // Configuration
  const config = createPathAwareDropdown()
    .criteria([{ id: "binding-uuid", name: "Binding Name" }])
    .path("binding-uuid", (path) =>
      path.column(pathOperators(OPERATORS.STRING_EXTENDED)).column(
        // Checkbox for multi-select operators
        column
          .checkbox("Select Binding", bindingItems)
          .withCondition(
            conditions.includesAny(1, "is-one-of", "is-not-one-of"),
          )
          .withPlaceholder("Search"),
        // Search for single select and text operators
        column
          .search("Select Binding", bindingItems)
          .withCondition(
            conditions.includesAny(
              1,
              "equals",
              "does-not-equal",
              "contains",
              "does-not-contain",
              "starts-with",
              "ends-with",
            ),
          )
          .withPlaceholder("Search or enter value")
          .allowCustomValue(),
      ),
    )
    .build();

  const handleSearch = useDebouncedCallback(
    (searchTerm: string, columnIndex: number) => {
      const value = searchTerm.trim();

      startTransition(() => {
        // Binding path has: 0=criteria, 1=operator, 2=value
        // Only refetch when searching in the value column
        if (columnIndex === 2) {
          const searchFilter = value
            ? { single: { name: "name", value } }
            : null;
          refetchBindings({
            filter: combineFilters(searchFilter),
          });
        }
      });
    },
    300,
  );

  const handleScrollEnd = (columnIndex: number) => {
    // Binding path has: 0=criteria, 1=operator, 2=value
    // Only load more when scrolling the value column
    if (columnIndex === 2 && hasNextBindings) {
      loadNextBindings(100);
    }
  };

  const { criteriaFilters, handleChange, handleRemove, initialSelections } =
    useSteppedMultiColumnDropdown({
      handleSelectionsChange,
      initialCriteriaFilters,
      defaultSelections: DEFAULT_SELECTIONS,
    });

  return (
    <FilterCriteriaCard
      helperTextOptions={["Binding Name"]}
      onRemoveCriterion={handleRemove}
      selectedCriteria={criteriaFilters}
      title="Binding"
    >
      <SteppedMultiColumnDropdown
        config={config}
        initialSelections={initialSelections}
        onChangeStructured={handleChange}
        onScrollEnd={handleScrollEnd}
        onSearch={handleSearch}
      />
    </FilterCriteriaCard>
  );
}
